"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { ArrowLeft, ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import type { Locale } from "@/i18n/routing";
import { BookingLink } from "@/components/ui/booking-link";
import type { Benefit } from "@/content/home";
import { hotlines, type LocaleKey } from "@/content/global";
import { PhoneCopyLink } from "@/components/layout/phone-copy-link";
import type { SectionsContent } from "@/content/sections";
import { t, type Localized } from "@/lib/content";
import { cn } from "@/lib/cn";
import { blurImageProps } from "@/lib/image-blur";

const photoSrc: Record<string, { src: string; alt: string }> = {
  "fixed-price":         { src: "/img/benefits/fixed-price.png",         alt: "Stoppuhr vor ISG-LKW" },
  "direct-no-hub":       { src: "/img/benefits/direct-no-hub.png",       alt: "ISG-Fahrer auf der Autobahn Richtung Stuttgart" },
  "personal-dispatcher": { src: "/img/benefits/personal-dispatcher.png", alt: "ISG-Disponentin am Schreibtisch mit Routenkarte" },
  "punctual-stat":       { src: "/img/benefits/punctual-stat.png",       alt: "Verladung an der Rampe vor ISG-LKW" },
  "insurance":           { src: "/img/benefits/insurance.png",           alt: "Ladungssicherung mit Spanngurten" },
  "tracking-portal":     { src: "/img/benefits/tracking-portal.png",     alt: "ISG-Kundenportal mit Routenansicht auf MacBook" },
};

const tabLabel: Record<string, Localized> = {
  "fixed-price":         { de: "Festpreis",     en: "Fixed price",   fr: "Prix fixe",      it: "Prezzo fisso",     nl: "Vaste prijs",   pl: "Cena stała",     es: "Precio fijo",     cs: "Pevná cena",   zh: "固定价格", da: "Fastpris" },
  "direct-no-hub":       { de: "Direktfahrt",   en: "Direct trip",   fr: "Trajet direct",  it: "Tragitto diretto", nl: "Directe rit",   pl: "Bezpośrednio",   es: "Trayecto directo", cs: "Přímá jízda",  zh: "直达运输", da: "Direkte tur" },
  "personal-dispatcher": { de: "Disponent",     en: "Dispatcher",    fr: "Répartiteur",    it: "Dispatcher",       nl: "Dispatcher",    pl: "Dyspozytor",     es: "Despachador",     cs: "Dispečer",     zh: "调度员", da: "Disponent" },
  "punctual-stat":       { de: "Pünktlich",     en: "On time",       fr: "Ponctuel",       it: "Puntuale",         nl: "Op tijd",       pl: "Punktualnie",    es: "Puntualidad",     cs: "Včas",         zh: "准时", da: "Til tiden" },
  "insurance":           { de: "Versicherung",  en: "Insurance",     fr: "Assurance",      it: "Assicurazione",    nl: "Verzekering",   pl: "Ubezpieczenie",  es: "Seguro",          cs: "Pojištění",    zh: "保险", da: "Forsikring" },
  "tracking-portal":     { de: "Portal",        en: "Portal",        fr: "Portail",        it: "Portale",          nl: "Portaal",       pl: "Portal",         es: "Portal",          cs: "Portál",       zh: "门户", da: "Portal" },
};

const ROTATE_MS = 4500;

export function BenefitsGrid({
  benefits,
  benefitsSection,
}: {
  benefits: Benefit[];
  benefitsSection: SectionsContent["benefitsSection"];
}) {
  const [active, setActive] = useState(0);
  const locale = useLocale() as Locale;
  const nav = useTranslations("Nav");
  const line = hotlines[locale as LocaleKey] ?? hotlines.de;
  const total = benefits.length.toString().padStart(2, "0");

  const prev = () => setActive((a) => (a - 1 + benefits.length) % benefits.length);
  const next = () => setActive((a) => (a + 1) % benefits.length);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setTimeout(() => {
      setActive((a) => (a + 1) % benefits.length);
    }, ROTATE_MS);
    return () => window.clearTimeout(id);
  }, [active]);

  return (
    <section id="vorteile" className="section border-y border-line-200 bg-canvas">
      <div className="container">
        <div className="grid items-end gap-8 lg:grid-cols-[1fr_auto]">
          <div className="max-w-3xl">
            <p className="kicker">{t(benefitsSection.kicker, locale) as string}</p>
            <h2 className="mt-5 text-[1.75rem] leading-[1.06] tracking-[-0.025em] md:text-[2.5rem] lg:text-[3rem] xl:text-[3.5rem]">
              {t(benefitsSection.title, locale) as string}
            </h2>
            <p className="mt-6 max-w-[60ch] text-base text-ink-700 md:text-lg leading-relaxed">
              {t(benefitsSection.body, locale) as string}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <BookingLink href="/angebot" className="btn-primary btn-lg">
                {nav("ctaQuote")}
                <ArrowUpRight className="h-4 w-4" aria-hidden />
              </BookingLink>
              <PhoneCopyLink href={line.href} number={line.number} className="btn-secondary btn-lg w-full">
                <Phone className="h-4 w-4" aria-hidden />
                <span className="tabnums">{line.number}</span>
              </PhoneCopyLink>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={prev}
              className="grid h-12 w-12 place-items-center rounded-full border border-line-200 text-ink-700 transition-colors hover:border-brand-600 hover:text-brand-600"
              aria-label={t(benefitsSection.prevAria, locale) as string}
            >
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={next}
              className="grid h-12 w-12 place-items-center rounded-full border border-line-200 text-ink-700 transition-colors hover:border-brand-600 hover:text-brand-600"
              aria-label={t(benefitsSection.nextAria, locale) as string}
            >
              <ArrowRight className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>

        <div
          aria-live="polite"
          className="mt-16 grid items-center gap-10 lg:mt-20 lg:grid-cols-2 lg:gap-16"
        >
          <div className="relative aspect-[5/4] w-full overflow-hidden rounded-2xl bg-soft">
            {(() => {
              // Render only the active slide's image. A keyed single <Image>
              // can never desync from `active` (unlike a stacked opacity
              // cross-fade, which the React-compiler memoization could freeze).
              const bn = benefits[active];
              const ph = bn ? photoSrc[bn.id] : undefined;
              if (!ph) return null;
              return (
                <Image
                  key={bn.id}
                  src={ph.src}
                  {...blurImageProps(ph.src)}
                  alt={ph.alt}
                  fill
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  priority
                  className="object-cover animate-[fadeIn_400ms_ease-out]"
                />
              );
            })()}
          </div>

          <div className="relative grid">
            {benefits.map((bn, i) => {
              const isActive = i === active;
              const num = (i + 1).toString().padStart(2, "0");
              return (
                <div
                  key={bn.id}
                  aria-hidden={!isActive}
                  className={cn(
                    "max-w-xl transition-opacity duration-500 ease-out [grid-area:1/1]",
                    isActive ? "opacity-100" : "pointer-events-none opacity-0",
                  )}
                >
                  <p className="kicker tabnums">
                    {num} <span className="text-ink-500">/ {total}</span>
                  </p>
                  <h3 className="mt-4 text-[1.875rem] font-semibold tracking-[-0.02em] md:text-[2.25rem] lg:text-[2.5rem]">
                    {t(bn.title, locale) as string}
                  </h3>
                  <p className="mt-5 text-base leading-relaxed text-ink-700 md:text-lg">
                    {t(bn.body, locale) as string}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-14 rounded-2xl border border-line-200 bg-soft p-1.5 lg:mt-16">
          <ol
            role="tablist"
            aria-label="Benefits"
            className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-6"
          >
            {benefits.map((bn, i) => {
              const isActive = i === active;
              const label = tabLabel[bn.id];
              return (
                <li key={bn.id} role="presentation">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActive(i)}
                    className={cn(
                      "relative flex h-full w-full items-center gap-3 overflow-hidden rounded-xl px-4 py-3.5 text-left transition-all duration-200",
                      isActive
                        ? "bg-canvas shadow-[0_1px_2px_rgba(11,18,32,0.04),0_8px_24px_-12px_rgba(11,18,32,0.16)]"
                        : "bg-transparent hover:bg-canvas/60",
                    )}
                  >
                    <span
                      className={cn(
                        "font-display text-[11px] font-semibold uppercase tracking-[0.16em] tabnums transition-colors",
                        // brand-700 (#E60000) clears WCAG AA 4.5:1 on white
                        // for 11 px small-bold text where brand-600 (#FF0000)
                        // falls short (only ~4.0:1).
                        isActive ? "text-brand-700" : "text-ink-500",
                      )}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={cn(
                        "truncate text-sm leading-snug transition-colors",
                        isActive ? "font-semibold text-ink-900" : "font-medium text-ink-700",
                      )}
                    >
                      {label ? (t(label, locale) as string) : (t(bn.title, locale) as string)}
                    </span>

                    {isActive ? (
                      <span
                        key={active}
                        aria-hidden
                        className="absolute inset-x-0 bottom-0 h-[3px] origin-left bg-brand-600"
                        style={{ animation: `growBar ${ROTATE_MS}ms linear forwards` }}
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
